export interface BidLot {
  name: string
  summary: string
  products: string[]
  useCases: string
}

export interface Bid {
  id: string
  reference: string
  title: string
  category: string
  publicationDate: string
  deadline: string
  status: 'open' | 'closing-soon' | 'closed'
  description: string
  estimatedValue?: string
  lots?: BidLot[]
}

export const bids: Bid[] = [
  {
    id: '1',
    reference: 'AU-AFRI-2026-001',
    title:
      'Framework Agreement for Medical Equipment Supply to AU Health Partnership Programme (Lots 1–4)',
    category: 'Health & Medical',
    publicationDate: '2026-04-15',
    deadline: '2026-06-30',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of medical equipment in support of the AU Health Systems Strengthening Initiative across 15 member states. Awarded across four independent lots — suppliers may bid on one or more. Specialist single-lot bids from African manufacturers and regional health-sector SMEs are encouraged. All equipment must meet WHO and international medical-device safety standards.',
    estimatedValue: '£2,500,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Diagnostic Equipment',
        summary: 'Imaging, point-of-care diagnostics, and laboratory analysers',
        products: [
          'Imaging systems (X-ray, ultrasound)',
          'ECG and ultrasound units',
          'Point-of-care diagnostic devices',
          'Laboratory analysers',
        ],
        useCases:
          'Primary care facilities and referral hospitals across AU member states.',
      },
      {
        name: 'Lot 2 — Surgical & Theatre Equipment',
        summary: 'Operating theatre, sterilisation, and anaesthesia equipment',
        products: [
          'Operating-theatre equipment',
          'Sterilisation systems',
          'Surgical instruments',
          'Anaesthesia machines',
        ],
        useCases: 'AU-supported hospitals and tertiary care centres.',
      },
      {
        name: 'Lot 3 — Laboratory & Pathology Equipment',
        summary: 'Laboratory instruments and reagent supply chains',
        products: [
          'Microscopes',
          'Centrifuges',
          'Sample-storage systems',
          'Reagent supply chains',
        ],
        useCases:
          'Diagnostic laboratories and disease-surveillance facilities.',
      },
      {
        name: 'Lot 4 — PPE & Medical Consumables',
        summary: 'Routine consumables and protective equipment',
        products: [
          'Gloves, masks, gowns',
          'Syringes and routine medical consumables',
          'Infection-control supplies',
        ],
        useCases:
          'Hospitals, field clinics, and emergency-response operations.',
      },
    ],
  },
  {
    id: '2',
    reference: 'AU-AFRI-2026-002',
    title: 'Construction of Trade Facilitation Centre in Accra, Ghana',
    category: 'Infrastructure',
    publicationDate: '2026-04-20',
    deadline: '2026-06-15',
    status: 'closing-soon',
    description:
      "Design-build single-contract tender for a modern Trade Facilitation Centre in Accra supporting AfCFTA implementation and Africa's global trade partnerships. Scope includes architectural design, civil and structural works, MEP, ICT infrastructure, fit-out, landscaping, and a 12-month defects-liability period. Bidders must demonstrate prior delivery of comparable institutional buildings, hold relevant ISO certifications, and submit a local-content plan prioritising Ghanaian and African subcontractors and labour.",
    estimatedValue: '£8,750,000',
  },
  {
    id: '3',
    reference: 'AU-AFRI-2026-003',
    title:
      'Framework Agreement for Digital Skills Training Programme Delivery (Lots 1–3)',
    category: 'Education & Training',
    publicationDate: '2026-04-10',
    deadline: '2026-07-10',
    status: 'open',
    description:
      'Multi-year framework agreement to deliver digital skills training to 5,000 young professionals across AU member states. Awarded across three independent lots — providers may bid on one or more. African training institutions and SMEs are encouraged. All providers must demonstrate accredited curriculum and gender-inclusive delivery.',
    estimatedValue: '£1,200,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — AI, Data Science & Analytics',
        summary: 'Advanced AI, machine learning, and data analytics curriculum',
        products: [
          'AI & machine learning training tracks',
          'Data science and analytics curriculum',
          'Capstone projects with AU partner institutions',
        ],
        useCases:
          'Mid-career upskilling and emerging-talent acceleration programmes.',
      },
      {
        name: 'Lot 2 — Software Development & Cloud Engineering',
        summary: 'Full-stack and cloud engineering bootcamps with placements',
        products: [
          'Full-stack software development bootcamps',
          'DevOps and cloud engineering training',
          'Industry mentorship and employer placement pathways',
        ],
        useCases:
          'Graduate-level cohorts and career transitions into the tech sector.',
      },
      {
        name: 'Lot 3 — Digital Literacy & Foundational ICT',
        summary: 'Entry-level digital and productivity skills',
        products: [
          'Foundational digital literacy programmes',
          'Productivity tools training',
          'Entry-level ICT skills curriculum',
        ],
        useCases: 'Underserved youth in rural and peri-urban communities.',
      },
    ],
  },
  {
    id: '4',
    reference: 'AU-AFRI-2026-004',
    title: 'Climate Finance Advisory Services',
    category: 'Consulting Services',
    publicationDate: '2026-03-28',
    deadline: '2026-06-05',
    status: 'closing-soon',
    description:
      'Single-contract advisory engagement for structuring climate finance instruments and green bonds for African infrastructure and renewable-energy projects. Scope includes market-sounding, instrument design, MRV frameworks, investor-engagement strategy, and capacity-building for AU member-state finance ministries. Bidders must demonstrate prior climate-finance transaction experience and field a multi-disciplinary team including African climate-finance specialists.',
    estimatedValue: '£650,000',
  },
  {
    id: '5',
    reference: 'AU-AFRI-2026-005',
    title:
      'Framework Agreement for Agricultural Technology Transfer Programme (Lots 1–3)',
    category: 'Agriculture',
    publicationDate: '2026-04-05',
    deadline: '2026-06-10',
    status: 'open',
    description:
      'Multi-year framework agreement for precision-agriculture technologies and training for smallholder farmers in East Africa. Awarded across three independent lots. African agri-tech SMEs and farmer-cooperative partnerships are strongly encouraged. Deliverables must align with climate-smart agriculture principles and the AU CAADP framework.',
    estimatedValue: '£3,200,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Precision Agriculture Technology Supply',
        summary: 'Sensors, irrigation, satellite imagery, and farm software',
        products: [
          'Soil-sensor systems',
          'Irrigation-control equipment',
          'Satellite-imagery subscriptions',
          'Farm-management software platforms',
        ],
        useCases:
          'Equipping smallholder cooperatives with precision-ag capability.',
      },
      {
        name: 'Lot 2 — Farmer Training & Extension Services',
        summary: 'On-the-ground training and extension support',
        products: [
          'Local-language curriculum development',
          'Demonstration plot programmes',
          'Gender-inclusive participation models',
          'Extension officer training of trainers',
        ],
        useCases:
          'Field-level capacity building across East African districts.',
      },
      {
        name: 'Lot 3 — Mechanisation Equipment & Inputs',
        summary:
          'Small-scale mechanisation, seeds, and climate-resilient inputs',
        products: [
          'Small-scale mechanisation equipment',
          'Certified seeds',
          'Climate-resilient agricultural inputs',
        ],
        useCases:
          'Distributed through farmer-cooperative channels for equitable access.',
      },
    ],
  },
  {
    id: '6',
    reference: 'AU-AFRI-2025-089',
    title: 'Renewable Energy Infrastructure Assessment (10 Member States)',
    category: 'Energy',
    publicationDate: '2025-12-10',
    deadline: '2026-02-15',
    status: 'closed',
    description:
      'Single-contract feasibility and assessment engagement covering renewable-energy infrastructure needs across 10 AU member states. Scope included resource assessment (solar, wind, hydro, geothermal), grid-readiness analysis, regulatory benchmarking, investment-pipeline identification, and a consolidated continental opportunity report. Tender now closed; award outcome published on the Contract Awards page.',
    estimatedValue: '£450,000',
  },
  {
    id: '7',
    reference: 'AU-AFRI-2026-006',
    title: 'Diaspora Engagement Platform Development',
    category: 'Technology',
    publicationDate: '2026-04-18',
    deadline: '2026-06-05',
    status: 'open',
    description:
      'Single-contract software development engagement for the design, build, and deployment of a digital platform connecting the African diaspora worldwide with investment, partnership, and remittance-linked opportunities in Africa. Scope includes UX design, web and mobile applications, identity verification, secure payments integration, multi-language support (EN/FR/ES/AR/PT), WCAG 2.1 AA accessibility, a 24-month support and maintenance phase, and capacity transfer to an AU-designated operations team.',
    estimatedValue: '£850,000',
  },
  {
    id: '8',
    reference: 'AU-AFRI-2026-007',
    title:
      'Framework Agreement for Women Entrepreneurs Support Programme (Lots 1–3)',
    category: 'Economic Development',
    publicationDate: '2026-04-12',
    deadline: '2026-07-22',
    status: 'closing-soon',
    description:
      'Multi-year framework agreement to deliver a comprehensive support programme for 1,000 women entrepreneurs across AU member states. Awarded across three independent lots. African women-led organisations and SMEs are strongly encouraged. All providers must demonstrate measurable gender-impact outcomes and business-survival tracking.',
    estimatedValue: '£1,800,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Business Training & Skills Development',
        summary:
          'Entrepreneurship, financial literacy, and sector-specific training',
        products: [
          'Tailored entrepreneurship training',
          'Financial-literacy programmes',
          'Sector-specific capacity-building',
          'In-person and online cohort delivery',
        ],
        useCases:
          'Foundational skills uplift for women-led ventures across all sectors.',
      },
      {
        name: 'Lot 2 — Mentorship & Networking',
        summary: 'Mentor pairings, peer networks, and regional summits',
        products: [
          'Structured mentorship pairings with senior African business leaders',
          'Peer-network programmes',
          'Regional networking summits',
        ],
        useCases:
          'Long-term relationship-building and ecosystem strengthening.',
      },
      {
        name: 'Lot 3 — Access to Finance Facilitation',
        summary: 'Investor matchmaking and finance facilitation',
        products: [
          'Investor matchmaking services',
          'Grant-application support',
          'Partnerships with African financial institutions',
          'Working capital and growth finance facilitation',
        ],
        useCases: 'Unlocking capital for scale-up of participating ventures.',
      },
    ],
  },
  {
    id: '9',
    reference: 'AU-AFRI-2025-092',
    title: 'Transport Corridor Feasibility Study (3 Member States)',
    category: 'Infrastructure',
    publicationDate: '2025-11-20',
    deadline: '2026-01-30',
    status: 'closed',
    description:
      'Single-contract feasibility engagement covering a multimodal transport corridor linking three AU member states. Scope included demand forecasting, route alternatives analysis, environmental and social impact screening, economic appraisal, financing-structure options, and a consolidated investment case. Tender now closed; award outcome published on the Contract Awards page.',
    estimatedValue: '£720,000',
  },
  {
    id: '10',
    reference: 'AU-AFRI-2026-008',
    title:
      'Framework Agreement for Youth Leadership Development Programme (Lots 1–3)',
    category: 'Education & Training',
    publicationDate: '2026-04-22',
    deadline: '2026-06-15',
    status: 'open',
    description:
      'Multi-year framework agreement to deliver leadership training and exchange opportunities for 200 emerging African leaders aged 25–35. Awarded across three independent lots. African training institutions, youth-led organisations, and pan-African networks are strongly encouraged. All providers must demonstrate measurable youth-empowerment outcomes and gender-balanced participation.',
    estimatedValue: '£950,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Leadership Training & Curriculum Delivery',
        summary: 'Cohort-based leadership curriculum across regional hubs',
        products: [
          'Public-sector leadership curriculum',
          'Civic engagement and inclusive governance modules',
          'Regional hub delivery infrastructure',
        ],
        useCases:
          'Building a cohort of next-generation African public-sector leaders.',
      },
      {
        name: 'Lot 2 — International Exchange & Placements',
        summary: 'Exchange logistics and host-organisation matching',
        products: [
          'Host-organisation matching',
          'Cross-continental placement logistics',
          'Intra-African exchange programmes',
        ],
        useCases: 'Exposure to global and intra-African leadership ecosystems.',
      },
      {
        name: 'Lot 3 — Mentorship & Alumni Network',
        summary: 'Senior mentorship and alumni platform',
        products: [
          'Senior-leader mentorship pairings',
          'Alumni network platform',
          'Peer engagement and career-progression support',
        ],
        useCases: 'Long-term post-programme leadership ecosystem building.',
      },
    ],
  },
  {
    id: '17',
    reference: 'AU-AFRI-2026-015',
    title:
      'Framework Agreement for Healthcare Beds, Mattresses & Institutional Bedding (Lots 1–3)',
    category: 'Health & Medical',
    publicationDate: '2026-05-15',
    deadline: '2026-08-05',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of healthcare beds, mattresses, and institutional bedding to support AU healthcare, humanitarian, and staff-accommodation operations. Awarded across three independent lots. Specialist single-lot bids are encouraged to support African SME and regional manufacturer participation. All products must meet international safety, health, and fire-retardancy standards.',
    estimatedValue: '£3,750,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Healthcare & Rehabilitation Beds',
        summary:
          'Adjustable hospital beds, orthopedic mattresses, healthcare pillows',
        products: [
          'Adjustable and lift-enabled hospital beds',
          'Motorised bed frames',
          'Orthopedic support mattresses',
          'Healthcare comfort pillows',
        ],
        useCases:
          'AU-supported hospitals, clinics, and rehabilitation centres.',
      },
      {
        name: 'Lot 2 — Humanitarian & Refugee Bedding',
        summary:
          'Vacuum-packed and compression mattresses for compact logistics',
        products: [
          'Vacuum-packed mattresses',
          'Compression foam mattresses',
          'Basic institutional bedding',
        ],
        useCases:
          'Refugee and IDP camp deployment, humanitarian field operations, emergency response.',
      },
      {
        name: 'Lot 3 — Staff Accommodation Bedding',
        summary: 'Dormitory-grade mattresses and pillows',
        products: [
          'Spring and foam mattresses',
          'Orthopedic and memory-foam pillows',
          'Dormitory-grade bedding',
        ],
        useCases:
          'AU regional office staff housing, training centres, peacekeeping accommodation, youth programme dormitories.',
      },
    ],
  },
  {
    id: '11',
    reference: 'AU-AFRI-2026-009',
    title: 'Framework Agreement for Food, Beverages & FMCG Supply (Lots 1–6)',
    category: 'Food, Beverages & FMCG',
    publicationDate: '2026-05-02',
    deadline: '2026-07-30',
    status: 'open',
    description:
      'Multi-year framework agreement for the supply of food, beverages, and FMCG products to the African Union Commission. Awarded across six broad, independent product lots designed to cover the full institutional FMCG portfolio — diplomatic gifting, summit hospitality, office catering, staff welfare, school feeding, refugee and IDP camp deployment, humanitarian field operations, and emergency feeding. Specialist single-lot bids from African SME and regional manufacturers are strongly encouraged. All suppliers must meet international food safety, labelling, and traceability standards.',
    estimatedValue: '£3,850,000 (across all lots)',
    lots: [
      {
        name: 'Lot 1 — Hot Beverages',
        summary: 'Coffee, tea, and hot drink solutions',
        products: [
          'Instant coffee',
          'Ground coffee',
          'Ready coffee mixes',
          'Tea (black, green, herbal)',
          'Hot chocolate',
          'Institutional hot-beverage solutions',
        ],
        useCases:
          'AU headquarters, regional offices, summits, partner events, staff catering.',
      },
      {
        name: 'Lot 2 — Cold Beverages',
        summary:
          'Soft drinks, juices, energy & functional drinks, multipack supply',
        products: [
          'Carbonated soft drinks',
          'Flavoured beverages',
          'Zero-sugar drinks',
          'Juices',
          'Energy and functional drinks',
          'Performance / sports drinks',
          'Multipack beverage supply',
        ],
        useCases:
          'Summits, conferences, field operations, youth and sports programmes.',
      },
      {
        name: 'Lot 3 — Confectionery, Chocolate & Sweets',
        summary:
          'Diplomatic gifting confectionery + child welfare programme treats',
        products: [
          'Premium boxed chocolates (diplomatic gifting)',
          'Chocolate bars and tablets',
          'Chocolate snack bars',
          'Wafer chocolates',
          'Chocolate spreads',
          'Packaged confectionery',
          'Lollipops, fruit sweets, chewy confectionery',
          'Mint and breath-freshening products',
        ],
        useCases:
          'Diplomatic gifting, summit hospitality, school feeding, child welfare, humanitarian outreach (child-nutrition compliance where applicable).',
      },
      {
        name: 'Lot 4 — Biscuits, Cookies, Wafers & Snack Bars',
        summary: 'Sweet baked snacks and snack bars',
        products: [
          'Sandwich biscuits',
          'Cream-filled biscuits',
          'Wafer snacks',
          'Cookies',
          'Mini snack biscuits',
          'Granola bars',
          'Snack bars',
        ],
        useCases:
          'Office catering, meetings, conferences, youth programme refreshments.',
      },
      {
        name: 'Lot 5 — Savoury Snacks & Seeds',
        summary: 'Roasted seeds, nuts, savoury snacks and crackers',
        products: [
          'Roasted sunflower seeds',
          'Salted and unsalted nuts',
          'Packaged savoury snacks',
          'Savoury crackers',
          'Institutional refreshment products',
        ],
        useCases:
          'Staff welfare, hospitality, retail concessions within AU facilities, field-operation provisioning.',
      },
      {
        name: 'Lot 6 — Convenience Meals & Ready-to-Eat',
        summary:
          'Instant noodles, ready pasta, shelf-stable meals, emergency feeding',
        products: [
          'Instant noodles',
          'Cup and bowl noodles',
          'Ready-to-eat ramen meals',
          'Ready pasta meals',
          'Protein pasta products',
          'Convenience pasta bowls',
          'Shelf-stable meals',
          'Nutritionally balanced convenience meals',
          'Quick-service meal solutions',
        ],
        useCases:
          'Institutional catering, staff feeding programmes, training centres, emergency food support, humanitarian field deployments.',
      },
    ],
  },
]
